import { useEffect, useRef, useState } from 'react';
import { StationeryDraftFormat } from '@graphql/generated-base';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { addError } from '@shared/utils/logger';
import { useUpdateStationeryDraftProductMutation } from '@apps/card/Card.graphql.generated';
import { useUpdateStationeryDraftMutation } from '../../CardCustomizer.graphql.generated';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useEditingMode } from '../../useEditingMode';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCustomizationContext } from '../../useCardCustomization';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useDraftSaverCancel } from '../../useDraftSaver';

type UseProductChoiceGroupArgs = {
  draftId: string;
};

export const useProductChoiceGroup = ({ draftId }: UseProductChoiceGroupArgs) => {
  const [themeId] = useCustomizationContext(d => d.template.themeId);
  const [draftConfig] = useCustomizationContext(d => d.config);
  const { products, activeProduct } = useStationeryProductPricingContext();
  const { mode } = useEditingMode();
  const { customizerProductChoiceGroupClicked } = useCardTelemetry();
  const { goToCustomizeDraft, goToDigitalCustomizeDraft } = useCardsRouterContext();
  const { eventInfo } = useEventInfo();
  const cancelPendingSave = useDraftSaverCancel();

  const [updateDraftProduct] = useUpdateStationeryDraftProductMutation();
  const [saveDraft] = useUpdateStationeryDraftMutation();

  const [switching, setSwitching] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasPaperProduct = products.some(p => p.productType === 'stationeryCard');
  const hasDigitalProduct = products.some(p => p.productType === 'digitalCard');

  const canSelectProduct = mode === 'draft' && hasPaperProduct && hasDigitalProduct;

  const selectProduct = useEventCallback(async (targetProductId: string) => {
    if (!canSelectProduct || switching || targetProductId === activeProduct.id) return;
    if (!eventInfo?.eventId) return;

    const targetProduct = products.find(p => p.id === targetProductId);
    if (!targetProduct) return;

    const from = activeProduct.productType === 'stationeryCard' ? 'paper' : 'premiumDigital';
    const to = targetProduct.productType === 'stationeryCard' ? 'paper' : 'premiumDigital';

    setSwitching(true);
    try {
      // Persist the latest local edits before switching formats so the BE converts
      // the most recent cardJSON, not whatever was last throttle-saved.
      await saveDraft({
        variables: { payload: { id: draftId, eventId: eventInfo.eventId, cardJSON: draftConfig } },
      });

      // Cancel any pending throttled save so it cannot fire after the BE has
      // converted the cardJSON — the explicit save above already has the latest state.
      cancelPendingSave();

      customizerProductChoiceGroupClicked({ from, to, themeId, draftId });

      const result = await updateDraftProduct({
        variables: { data: { id: draftId, productId: targetProductId } },
      });

      const newFormat = result.data?.updateStationeryDraftProduct.format;
      if (newFormat === StationeryDraftFormat.print) {
        goToCustomizeDraft(draftId);
      } else if (newFormat === StationeryDraftFormat.digital) {
        goToDigitalCustomizeDraft(draftId);
      }
    } catch (error) {
      addError(error, { context: 'useProductChoiceGroup', draftId, targetProductId });
    } finally {
      if (isMountedRef.current) {
        setSwitching(false);
      }
    }
  });

  return { selectProduct, isSwitching: switching, canSelectProduct };
};
